import { create } from 'zustand';

const usePagesStore = create((set, get) => ({
  // State
  pages: [],
  currentPage: null,
  currentRevision: null,
  revisions: [],
  paragraphs: [],
  isLoading: false,
  error: null,

  // Actions
  setLoading: (isLoading) => set({ isLoading }),
  setError: (error) => set({ error }),
  clearError: () => set({ error: null }),
  setCurrentPage: (page) => set({ currentPage: page }),
  setCurrentRevision: (revision) => set({ currentRevision: revision }),

  // Fetch all pages with optional search and status filter
  fetchPages: async (accessToken, search = null, status = null) => {
    set({ isLoading: true, error: null });

    let url = '/api/pages';
    const params = [];
    if (search) params.push(`search=${encodeURIComponent(search)}`);
    if (status) params.push(`status=${encodeURIComponent(status)}`);
    if (params.length > 0) url += `?${params.join('&')}`;

    try {
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch pages');
      }

      set({
        pages: data.pages || [],
        isLoading: false,
      });

      return { success: true, pages: data.pages };
    } catch (error) {
      set({
        isLoading: false,
        error: error.message,
      });
      return { success: false, error: error.message };
    }
  },

  // Fetch single page details
  fetchPage: async (pageId, accessToken) => {
    set({ isLoading: true, error: null });

    try {
      const response = await fetch(`/api/pages/${pageId}`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch page');
      }

      set({
        currentPage: data.page,
        currentRevision: data.published_revision,
        isLoading: false,
      });

      return { success: true, page: data.page, publishedRevision: data.published_revision };
    } catch (error) {
      set({
        isLoading: false,
        error: error.message,
      });
      return { success: false, error: error.message };
    }
  },

  // Create new page
  createPage: async (pageData, accessToken) => {
    set({ error: null });

    try {
      const response = await fetch('/api/pages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify(pageData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to create page');
      }

      // Add to pages list
      set((state) => ({
        pages: [data.page, ...state.pages],
        currentPage: data.page,
      }));

      return { success: true, page: data.page, revisionId: data.revision_id };
    } catch (error) {
      set({ error: error.message });
      return { success: false, error: error.message };
    }
  },

  // Update page metadata
  updatePage: async (pageId, updates, accessToken) => {
    set({ error: null });

    try {
      const response = await fetch(`/api/pages/${pageId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify(updates),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to update page');
      }

      // Update in pages list
      set((state) => ({
        pages: state.pages.map((p) => (p.id === pageId ? data.page : p)),
        currentPage: state.currentPage?.id === pageId ? data.page : state.currentPage,
      }));

      return { success: true, page: data.page };
    } catch (error) {
      set({ error: error.message });
      return { success: false, error: error.message };
    }
  },

  // Fetch revision history for a page
  fetchRevisions: async (pageId, accessToken) => {
    set({ isLoading: true, error: null });

    try {
      const response = await fetch(`/api/pages/${pageId}/revisions`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch revisions');
      }

      set({
        revisions: data.revisions || [],
        isLoading: false,
      });

      return { success: true, revisions: data.revisions };
    } catch (error) {
      set({
        isLoading: false,
        error: error.message,
      });
      return { success: false, error: error.message };
    }
  },

  // Publish a revision
  publishRevision: async (pageId, revisionId, accessToken) => {
    set({ error: null });

    try {
      const response = await fetch(`/api/pages/${pageId}/revisions/${revisionId}/publish`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to publish revision');
      }

      // Refresh revisions to see updated status
      await get().fetchRevisions(pageId, accessToken);

      return { success: true, message: data.message };
    } catch (error) {
      set({ error: error.message });
      return { success: false, error: error.message };
    }
  },

  // Fetch paragraph tree for a revision
  fetchParagraphs: async (pageId, revisionId, accessToken) => {
    set({ isLoading: true, error: null });

    try {
      const response = await fetch(`/api/pages/${pageId}/revisions/${revisionId}/paragraphs`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch paragraphs');
      }

      set({
        paragraphs: data.paragraphs || [],
        currentRevision: data.revision || null,
        isLoading: false,
      });

      return { success: true, paragraphs: data.paragraphs };
    } catch (error) {
      set({
        isLoading: false,
        error: error.message,
      });
      return { success: false, error: error.message };
    }
  },

  // Create new paragraph
  createParagraph: async (paragraphData, accessToken) => {
    set({ error: null });

    try {
      const response = await fetch('/api/paragraphs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify(paragraphData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to create paragraph');
      }

      return { success: true, paragraph: data.paragraph };
    } catch (error) {
      set({ error: error.message });
      return { success: false, error: error.message };
    }
  },

  // Update paragraph
  updateParagraph: async (paragraphId, updates, accessToken) => {
    set({ error: null });

    try {
      const response = await fetch(`/api/paragraphs/${paragraphId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify(updates),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to update paragraph');
      }

      return { success: true, paragraph: data.paragraph };
    } catch (error) {
      set({ error: error.message });
      return { success: false, error: error.message };
    }
  },

  // Delete paragraph
  deleteParagraph: async (paragraphId, accessToken) => {
    set({ error: null });

    try {
      const response = await fetch(`/api/paragraphs/${paragraphId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to delete paragraph');
      }

      return { success: true, message: data.message };
    } catch (error) {
      set({ error: error.message });
      return { success: false, error: error.message };
    }
  },

  // Reorder paragraphs
  reorderParagraphs: async (paragraphsArray, accessToken) => {
    set({ error: null });

    try {
      const response = await fetch('/api/paragraphs/reorder', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ paragraphs: paragraphsArray }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to reorder paragraphs');
      }

      return { success: true, message: data.message };
    } catch (error) {
      set({ error: error.message });
      return { success: false, error: error.message };
    }
  },

  // Create draft from published revision (deep copy)
  createDraft: async (pageId, accessToken) => {
    set({ error: null });

    try {
      const response = await fetch(`/api/pages/${pageId}/revisions/create-draft`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to create draft');
      }

      return { success: true, revisionId: data.revision_id, message: data.message };
    } catch (error) {
      set({ error: error.message });
      return { success: false, error: error.message };
    }
  },

  // Publish a draft revision
  publishRevision: async (pageId, revisionId, accessToken) => {
    set({ error: null });

    try {
      const response = await fetch(`/api/pages/${pageId}/revisions/${revisionId}/publish`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to publish revision');
      }

      return { success: true, message: data.message };
    } catch (error) {
      set({ error: error.message });
      return { success: false, error: error.message };
    }
  },

  // Discard a draft revision
  discardDraft: async (pageId, revisionId, accessToken) => {
    set({ error: null });

    try {
      const response = await fetch(`/api/pages/${pageId}/revisions/${revisionId}/discard`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to discard draft');
      }

      return { success: true, message: data.message };
    } catch (error) {
      set({ error: error.message });
      return { success: false, error: error.message };
    }
  },

  // Delete page (system pages only - admin)
  deletePage: async (pageId, accessToken) => {
    set({ error: null });

    try {
      const response = await fetch(`/api/pages/${pageId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to delete page');
      }

      // Remove from pages list
      set((state) => ({
        pages: state.pages.filter((p) => p.id !== pageId),
      }));

      return { success: true, message: data.message };
    } catch (error) {
      set({ error: error.message });
      return { success: false, error: error.message };
    }
  },
}));

export { usePagesStore };
